import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  Skeleton,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'src/redux/store';
import { fetchOrderById } from 'src/redux/features/order/orderSlice';
import { PATH_DASHBOARD } from 'src/routes/paths';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import Scrollbar from 'src/components/scrollbar';
import { OrderResponse } from 'src/@types/order/order.type';
import { useProductTranslation } from 'src/hooks/useProductTranslation';
import AiTranscriptAnalysis from './AiTranscriptAnalysis';

// dynamic taxes will be pulled from data

function fCurrency(value: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

export default function OrderDetailsPage() {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getName } = useProductTranslation();
  const { user } = useAuthContext();
  const [currentTab, setCurrentTab] = useState('details');

  const loading = useSelector((s) => s.orders.single.loading);
  const error = useSelector((s) => s.orders.single.error);
  const data = useSelector((s) => s.orders.single.data) as OrderResponse | null;

  useEffect(() => {
    if (orderId) {
      dispatch(fetchOrderById(orderId));
    }
  }, [dispatch, orderId]);

  const getBasePrice = (item: any) => {
    if (item.unitPrice != null) return Number(item.unitPrice);
    const isTrio = !!(item.trioSelections && item.trioSelections.length > 0);
    return isTrio ? Number(item.product.trioPrice || 0) : Number(item.product.price || 0);
  };

  const { subtotal, total, federalTax, provincialTax } = useMemo(() => {
    if (!data) return { subtotal: 0, total: 0, federalTax: 0, provincialTax: 0 };

    const sb = (data.orderedProducts || []).reduce((acc, item) => {
      const base = getBasePrice(item);
      const extras = (item.trioSelections || []).reduce((s, sel) => s + Number(sel.price || 0), 0);
      const line = (base + extras) * Number(item.quantity || 0);
      return acc + line;
    }, 0);

    const federalTaxRate = data.federalTaxRate ?? 5.0;
    const provincialTaxRate = data.provincialTaxRate ?? 9.975;

    const fedTax = sb * (federalTaxRate / 100);
    const provTax = sb * (provincialTaxRate / 100);

    return {
      subtotal: sb,
      federalTax: fedTax,
      provincialTax: provTax,
      total: sb + fedTax + provTax
    };
  }, [data]);

  const formatDate = (date: Date) =>
    new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(date));

  return (
    <>
      <Helmet>
        <title>Order Details</title>
      </Helmet>

      <Container maxWidth="lg">
        <CustomBreadcrumbs
          heading="Détails de la commande"
          links={[
            { name: 'ID de la commande' },
            { name: data ? `#${data.id}` : '...' },
          ]}
          action={
            <Button
              variant="outlined"
              onClick={() => {
                if (data?.parentOrgId && data?.subOrgId) {
                  const targetUrl = PATH_DASHBOARD.user.branch.details(data.parentOrgId, data.subOrgId);
                  navigate(`${targetUrl}?tab=orders`);
                } else {
                  navigate(-1);
                }
              }}
            >
              Retour aux commandes
            </Button>
          }
        />

        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab value="details" label="Détails de la commande" />
          {user?.role === 'SUPER_ADMIN' && (
            <Tab value="analysis" label="Analyse IA & Transcript" />
          )}
        </Tabs>

        {currentTab === 'details' && (
        <Grid container spacing={3}>
          {/* Summary Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2 }}>
              {loading || !data ? (
                <Stack spacing={1}>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="rectangular" height={120} />
                </Stack>
              ) : (
                <Stack spacing={1}>
                  <Typography variant="h6">La commande #{data.id}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Créé: {formatDate(data.createdAt)}
                  </Typography>
                  <Chip
                    label={String(data.orderState).toLowerCase().replace('_', ' ')}
                    color="primary"
                    size="small"
                    sx={{ alignSelf: 'flex-start', textTransform: 'capitalize' }}
                  />
                  <Divider sx={{ my: 1 }} />
                  <Stack spacing={0.5}>
                    <Typography variant="body2">Sous-total: {fCurrency(subtotal)}</Typography>
                    <Typography variant="body2">TPS ({data.federalTaxRate ?? 5}%): {fCurrency(federalTax ?? 0)}</Typography>
                    <Typography variant="body2">TVQ ({data.provincialTaxRate ?? 9.975}%): {fCurrency(provincialTax ?? 0)}</Typography>
                    <Typography variant="h6">Total: {fCurrency(total)}</Typography>
                  </Stack>
                </Stack>
              )}
            </Card>
          </Grid>

          {/* Items Card */}
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 0 }}>
              <Box sx={{ px: 2, py: 1, bgcolor: '#f5f9ff', borderBottom: '1px solid #e3f2fd' }}>
                <Typography variant="subtitle1" color="#1976d2">
                  {t('nav.order_products')}
                </Typography>
              </Box>
              <Scrollbar>
                <Box sx={{ p: 2 }}>
                  {loading || !data ? (
                    <Stack spacing={1}>
                      <Skeleton variant="rectangular" height={64} />
                      <Skeleton variant="rectangular" height={64} />
                      <Skeleton variant="rectangular" height={64} />
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      {data.orderedProducts.map((item) => {
                        const isTrio = !!(item.trioSelections && item.trioSelections.length > 0);
                        const base = getBasePrice(item);
                        const extras = (item.trioSelections || []).reduce((s, sel) => s + Number(sel.price || 0), 0);
                        const line = (base + extras) * Number(item.quantity || 0);

                        return (
                          <Box
                            key={item.id}
                            sx={{
                              p: 1.5,
                              border: '1px solid',
                              borderColor: '#eee',
                              borderRadius: 1,
                              '&:hover': { borderColor: '#e3f2fd', bgcolor: '#fafcff' },
                            }}
                          >
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                              <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    {getName(item.product.translations)}
                                  </Typography>
                                  {isTrio && (
                                    <Chip size="small" color="error" variant="outlined" label="Trio" />
                                  )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                  Qty {item.quantity} • Base {fCurrency(base)}
                                  {extras > 0 && ` • Extras ${fCurrency(extras)}`}
                                </Typography>
                                {isTrio && (item.trioSelections || []).length > 0 && (
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                                    {(item.trioSelections || []).map((sel) => (
                                      <Chip
                                        key={sel.id}
                                        label={`${sel.name} (+${fCurrency(Number(sel.price || 0))})`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ borderColor: '#d32f2f', color: '#d32f2f' }}
                                      />
                                    ))}
                                  </Stack>
                                )}
                              </Box>

                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {fCurrency(line)}
                              </Typography>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Scrollbar>
              <Divider sx={{ mt: 1 }} />
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Stack spacing={0.3} sx={{ minWidth: 240 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Subtotal
                    </Typography>
                    <Typography variant="body2">{fCurrency(subtotal)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      TPS ({data?.federalTaxRate ?? 5}%)
                    </Typography>
                    <Typography variant="body2">{fCurrency(federalTax ?? 0)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      TVQ ({data?.provincialTaxRate ?? 9.975}%)
                    </Typography>
                    <Typography variant="body2">{fCurrency(provincialTax ?? 0)}</Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Total
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {fCurrency(total)}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Card>
          </Grid>
        </Grid>
        )}

        {currentTab === 'analysis' && user?.role === 'SUPER_ADMIN' && (
          <AiTranscriptAnalysis steps={data?.steps || []} order={data} />
        )}
      </Container>
    </>
  );
}
